import { Client, ClientConfig, ClientGlobals } from "@searchspring/snap-client";
import {
	MetaResponseModel,
	SearchRequestModel,
	SearchResponseModel,
	SearchResponseModelResult,
} from "@searchspring/snapi-types";
import { indexOf, orderBy } from "lodash";
import { redirect } from "next/navigation";
import { IntelliSuggestItem, ProductData } from "../5874/types";
import {
	getProductsByIds,
	getProductsWithoutChildrenByIds,
} from "../bigcommerce/product";
import { StoreRegion } from "../builder/builder.config";
import { getConfig } from "./searchspring.config";
import {
	EnrichedRecommendCombinedRequestModel,
	EnrichedRecommendCombinedResponseModel,
	EnrichedSearchRequestModel,
} from "./types";
import { ProductAvailability, ProductCondition } from "../bigcommerce/enums";
import { cleanUrl } from "./utils";

export class SearchSpringClient extends Client {
	private store;
	private lang;

	siteId = "";

	constructor(
		store: StoreRegion,
		lang: string,
		globals: ClientGlobals,
		clientConfig?: ClientConfig,
	) {
		super(globals, clientConfig);

		this.store = store;
		this.lang = lang;
		this.siteId = globals.siteId;
	}

	/**
	 * Performs a search and gets results from SearchSpring
	 *
	 * @param {SearchRequestModel} [params] - Optional search parameters.
	 * @return {Promise<[MetaResponseModel, EnrichedSearchRequestModel]>} A promise that resolves to an array containing the meta response model and the enriched search request model.
	 */
	async baseSearch(
		params?: SearchRequestModel,
	): Promise<[MetaResponseModel, SearchResponseModel, number[]]> {
		const [meta, results] = await super.search(params);

		if (results.merchandising?.redirect) {
			redirect(`/${this.store}/${this.lang}${results.merchandising.redirect}`);
		}

		const response = results.results;

		if (!response) {
			return [
				meta,
				{
					...(results as any),
					results: [],
				},
				[],
			];
		}

		const productEntityIds = response
			.map((result) => {
				const id = result.mappings?.core?.uid;
				return id ? parseInt(id) : undefined;
			})
			.filter((value) => value !== undefined) as number[];

		return [meta, results, productEntityIds];
	}

	/**
	 * Performs a search with enriched results.
	 *
	 * @param {MetaResponseModel, SearchResponseModel, number[]} [params] - Params are returned from the baseSearch function
	 * @return {Promise<[MetaResponseModel, EnrichedSearchRequestModel]>} A promise that resolves to an array containing the meta response model and the enriched search request model.
	 */
	async enrichedSearch(
		meta: MetaResponseModel,
		results: SearchResponseModel,
		productEntityIds: number[],
	): Promise<[MetaResponseModel, EnrichedSearchRequestModel]> {
		if (!results.results) {
			return [
				meta,
				{
					...(results as any),
					results: [],
				},
			];
		}

		const intelData = results.results.map((r) => ({
			id: r.id,
			intellisuggestData: String(r.attributes?.intellisuggestData),
			intellisuggestSignature: String(r.attributes?.intellisuggestSignature),
		}));

		const products: ProductData[] | null =
			await getProductsByIds(productEntityIds);

		const updatedProducts = products?.map((product) => {
			const matchedIntelData = intelData.find(
				(intel) => intel.id && parseInt(intel.id) === product.id,
			);

			return matchedIntelData
				? {
						...product,
						intellisuggestSignature: matchedIntelData.intellisuggestSignature,
						intellisuggestData: matchedIntelData.intellisuggestData,
				  }
				: product;
		});

		const sortedResults: ProductData[] = orderBy(updatedProducts, (product) => {
			return indexOf(productEntityIds, product.id);
		});

		return [meta, { ...(results as any), results: sortedResults }];
	}

	async preliminarySearchFromBase(
		meta: MetaResponseModel,
		results: SearchResponseModel,
		productEntityIds: number[],
	): Promise<[MetaResponseModel, EnrichedSearchRequestModel]> {
		if (!results.results) {
			return [
				meta,
				{
					...(results as any),
					results: [],
				},
			];
		}

		const productsFromSearchSpring = results.results.map(
			transformResultsToProductData,
		);

		const sortedResults: ProductData[] = orderBy(
			productsFromSearchSpring,
			(product) => indexOf(productEntityIds, product.id),
		);

		return [meta, { ...(results as any), results: sortedResults }];
	}

	async preliminaryRecommend(
		params: EnrichedRecommendCombinedRequestModel,
	): Promise<EnrichedRecommendCombinedResponseModel> {
		const { meta, profile, results } = await super.recommend(params);

		if (!results) {
			return {
				meta,
				profile,
				results: [],
			};
		}

		const productsFromSearchSpring = results.map(transformResultsToProductData);

		return {
			meta,
			profile,
			results: productsFromSearchSpring,
		};
	}

	async enrichedRecommend(
		params: EnrichedRecommendCombinedRequestModel,
	): Promise<EnrichedRecommendCombinedResponseModel> {
		const { meta, profile, results } = await super.recommend(params);

		if (!results) {
			return {
				meta,
				profile,
				results: [],
			};
		}

		const productEntityIds = results
			.map((result) => {
				const id = result.mappings?.core?.uid;

				return id ? parseInt(id) : undefined;
			})
			.filter((value) => value !== undefined);

		const products = await getProductsByIds(productEntityIds as number[]);

		return {
			meta,
			profile,
			results: products!,
		};
	}

	async getSearchSuggestions(searchTerm: string): Promise<any> {
		const siteId = getConfig(this.store).globals.siteId;

		try {
			const response = await fetch(
				`https://${siteId}.a.searchspring.io/api/suggest/query?siteId=${siteId}&q=${encodeURIComponent(
					searchTerm,
				)}&limit=4`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
					},
				},
			);

			if (!response.ok) {
				throw new Error(
					`Error fetching search suggestions: ${response.statusText}`,
				);
			}

			const results = await response.json();

			const suggestions = [];
			if (results?.suggested?.text) suggestions.push(results?.suggested?.text);

			for (const alternatives of results?.alternatives as any[]) {
				suggestions.push(alternatives.text);
			}

			return suggestions;
		} catch (error) {
			console.error("Error fetching search suggestions:", error);
			throw error;
		}
	}

	async getSearchAutoCompleteProducts(searchTerm: string): Promise<any> {
		const siteId = getConfig(this.store).globals.siteId;

		try {
			const response = await fetch(
				`https://${siteId}.a.searchspring.io/api/search/autocomplete.json?siteId=${siteId}&q=${encodeURIComponent(
					searchTerm,
				)}&resultsFormat=json&limit=10`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
					},
				},
			);

			if (!response.ok) {
				throw new Error(
					`Error fetching search products: ${response.statusText}`,
				);
			}

			const results = await response.json();

			const productEntityIds = results?.results?.map(
				(result: { uid: string }) => {
					const id = result.uid;

					return id ? parseInt(id) : undefined;
				},
			);

			const products = await getProductsByIds(productEntityIds);

			return products;
		} catch (error) {
			console.error("Error fetching search products:", error);
			throw error;
		}
	}

	async getSearchAutoCompleteResults(
		searchTerm: string,
	): Promise<IntelliSuggestItem[]> {
		const siteId = getConfig(this.store).globals.siteId;

		try {
			const response = await fetch(
				`https://${siteId}.a.searchspring.io/api/search/autocomplete.json?siteId=${siteId}&q=${encodeURIComponent(
					searchTerm,
				)}&resultsFormat=json&limit=11`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
					},
				},
			);

			if (!response.ok) {
				throw new Error(
					`Error fetching search products: ${response.statusText}`,
				);
			}

			const results = await response.json();

			return results.results;
		} catch (error) {
			console.error("Error fetching search products:", error);
			throw error;
		}
	}

	async getSearchTrendingSuggestions(): Promise<any> {
		const siteId = getConfig(this.store).globals.siteId;

		try {
			const response = await fetch(
				`https://${siteId}.a.searchspring.io/api/suggest/trending?siteId=${siteId}&limit=4`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
					},
				},
			);

			if (!response.ok) {
				throw new Error(
					`Error fetching trending search suggestions: ${response.statusText}`,
				);
			}

			const results = await response.json();

			const productEntityIds = results?.results?.map(
				(result: { uid: string }) => {
					const id = result.uid;

					return id ? parseInt(id) : undefined;
				},
			);

			const products = await getProductsByIds(productEntityIds);

			return products;
		} catch (error) {
			console.error("Error fetching trending search suggestions:", error);
			throw error;
		}
	}
}

export const getClient = async (store: StoreRegion, lang: string) => {
	const configs = getConfig(store);
	const client = new Client(configs.globals, configs.clientConfig);
	return client;
};

export const getSearchSpringClient = (store: StoreRegion, lang: string) => {
	const configs = getConfig(store);

	const client = new SearchSpringClient(
		store,
		lang,
		configs.globals,
		configs.clientConfig,
	);

	return client;
};

export const fetchProductsFromIds = async (results: any) => {
	const productEntityIds = results?.results?.map((result: { uid: string }) => {
		const id = result.uid;

		return id ? parseInt(id) : undefined;
	});

	const products = await getProductsByIds(productEntityIds);

	return products;
};

export function transformResultsToProductData(
	result: SearchResponseModelResult,
) {
	const id = parseInt(result.id ?? "", 10);
	return {
		id,
		productId: id,
		createdAt: Date.now(),
		updatedAt: Date.now(),
		path: cleanUrl(result?.mappings?.core?.url ?? ""),
		sku: result?.mappings?.core?.sku,
		name: result?.mappings?.core?.name,
		description: result?.mappings?.core?.description,
		categories: [],
		options: [],
		purchasability: {},
		pricesIncTax: {
			price: { value: result?.mappings?.core?.price, currencyCode: "USD" },
			basePrice: { value: result?.mappings?.core?.price, currencyCode: "USD" },
		},
		pricesExTax: {
			price: { value: result?.mappings?.core?.price, currencyCode: "USD" },
			basePrice: { value: result?.mappings?.core?.price, currencyCode: "USD" },
		},
		price: result?.mappings?.core?.price,
		mapPrice: null,
		warranty: "",
		customFields: [],
		variants: [],
		seo: {
			title: result?.mappings?.core?.name,
			description: result?.mappings?.core?.description,
			keywords: result?.mappings?.core?.name
				?.replace(/ [|-].*$/, "")
				.split(" ")
				.join(","),
			url: result?.mappings?.core?.url,
		},
		mpn: result?.mappings?.core?.sku,
		brand: {
			name: result?.mappings?.core?.brand,
			path: `/brand/${result?.mappings?.core?.brand?.toLowerCase()}`,
			id: 0,
		},
		intellisuggestSignature: result?.attributes?.intellisuggestSignature,
		intellisuggestData: result?.attributes?.intellisuggestData,
		condition: ProductCondition.NEW,
		availability: ProductAvailability.AVAILABLE,
		isNotPurchasable: false,
		noAvailabilityMessage: "",
		plainTextDescription: result?.mappings?.core?.description,
		inventory: {
			isInStock: true,
			hasVariantInventory: false,
			aggregated: {
				availableToSell: 1,
				warningLevel: 10,
			},
		},
		meta: [],
		media: {
			images: [
				{
					id,
					description: result?.mappings?.core?.name,
					sortOrder: 0,
					__type: "Image",
					isThumbnail: true,
					url: {
						standard: result?.mappings?.core?.imageUrl,
						thumbnail: result?.mappings?.core?.thumbnailImageUrl,
						tiny: result?.mappings?.core?.thumbnailImageUrl,
						zoom: result?.mappings?.core?.imageUrl,
					},
				},
			],
			videos: [],
		},
		template: "product",
		_type: "Product",
		productClass: "",
		children: [],
		siblings: [],
		kitProducts: [],
		orderedQuantity: 0,
		shippedQuantity: 0,
		kitQuantity: 0,
		maxPurchaseQuantity: 0,
		minPurchaseQuantity: 1,
		isAvailableToCustomer: true,
	} as ProductData;
}
